import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { TbCalendarEvent, TbCalendarPlus, TbClock, TbMapPin, TbVideo, TbList, TbCalendar, TbPlus, TbChevronLeft, TbChevronRight, TbVideoCall, TbUsers, TbPhone } from 'react-icons/tb';
import ZoomImageImage from '../../../media/site_media/platform-icons/zoom.png';
import MicrosoftTeamsImage from '../../../media/site_media/platform-icons/microsoft-teams.png';
import GoogleMeetImage from '../../../media/site_media/platform-icons/google-meet.png';
import { stripHtmlTags } from '../../../utils/stripHtmlTags';
import { formatDateYMD } from '../../../i18n/getIntlLocale';
// Helper functions
const formatDateKey = (date) => {
   return formatDateYMD(date);
};

const parseDateKey = (key) => {
   const [year, month, day] = key.split('-').map(Number);
   return new Date(year, month - 1, day);
};

const formatEventDateLabel = (date) => formatDateYMD(date);

const formatFullDateLabel = (date) => formatDateYMD(date);

const formatEventTimeRange = (start, end) => {
   const pad = (n) => String(n).padStart(2, '0');
   const startLabel = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
   if (!end) {
      return startLabel;
   }
   const endLabel = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
   return `${startLabel} - ${endLabel}`;
};

const CommunityUpcomingEvents = ({ brandColor, events = [], onAddEvent, windowWidth, is_me }) => {
   const { t } = useTranslation();
   // Upcoming events UI state
   const [eventsView, setEventsView] = useState('list'); // 'list' | 'calendar'

   // Transform API events to component format
   const transformEvent = (apiEvent) => {
      const eventTime = new Date(apiEvent.event_time);
      // Default to 1 hour duration if no end time specified
      const endTime = new Date(eventTime.getTime() + 60 * 60 * 1000);
      
      // Determine platform from content_type
      let platform = 'Other';
      if (apiEvent.content_type === 'mgm') {
         platform = 'Google Meet';
      } else if (apiEvent.content_type === 'mzm') {
         platform = 'Zoom';
      } else if (apiEvent.content_type === 'mmt') {
         platform = 'Microsoft Teams';
      } else if (apiEvent.content_type === 'mot') {
         platform = 'Other';
      }

      // Extract title - use community_post_title if available, otherwise generate from text
      let title = apiEvent.community_post_title || '';
      if (!title && apiEvent.text) {
         // Try to extract a title from the text (first sentence or first 50 chars)
         const textMatch = apiEvent.text.match(/^[^.!?\n]+/);
         title = textMatch ? textMatch[0].trim() : apiEvent.text.substring(0, 50).trim();
         if (title.length > 60) {
            title = title.substring(0, 57) + '...';
         }
      }
      if (!title) {
         title = `Meeting - ${formatDateYMD(eventTime)}`;
      }

      return {
         id: apiEvent.id?.toString() || `event-${apiEvent.id}`,
         title: title,
         start: apiEvent.event_time,
         end: endTime.toISOString(),
         type: 'virtual', // All current events appear to be virtual
         platform: platform,
         description: apiEvent.text || '',
         link: apiEvent.link || '',
         user: apiEvent.user || apiEvent.user_display_name || '',
         user_icon: apiEvent.user_icon || ''
      };
   };

   const upcomingEvents = useMemo(() => {
      if (!events || events.length === 0) {
         return [];
      }

      const transformed = events
         .filter(event => event.is_example || (event.event_time && event.content_type && ['mgm', 'mzm', 'mmt', 'mot'].includes(event.content_type)))
         .map(event => event.is_example ? {
            id: event.id,
            title: event.community_post_title || 'Event',
            start: event.event_time,
            type: 'virtual',
            platform: event.content_type === 'mzm' ? 'Zoom' : event.content_type === 'mgm' ? 'Google Meet' : event.content_type === 'mmt' ? 'Microsoft Teams' : 'Other',
            link: event.link || '',
            icon: null,
            is_example: true,
         } : transformEvent(event))
         .filter(event => new Date(event.start) >= new Date(Date.now() - 24 * 60 * 60 * 1000));

      return transformed.sort((a, b) => new Date(a.start) - new Date(b.start));
   }, [events]);

   const [calendarMonth, setCalendarMonth] = useState(() => {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), 1);
   });

   const [selectedDateKey, setSelectedDateKey] = useState(() => {
      const now = new Date();
      return formatDateKey(now);
   });

   const eventsByDate = useMemo(() => {
      const grouped = {};
      upcomingEvents.forEach((event) => {
         const eventStart = new Date(event.start);
         const key = formatDateKey(eventStart);
         if (!grouped[key]) {
            grouped[key] = [];
         }
         grouped[key].push(event);
      });

      Object.keys(grouped).forEach((key) => {
         grouped[key].sort((a, b) => new Date(a.start) - new Date(b.start));
      });

      return grouped;
   }, [upcomingEvents]);

   const calendarDays = useMemo(() => {
      const days = [];
      const monthStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
      const startDate = new Date(monthStart);
      startDate.setDate(startDate.getDate() - startDate.getDay());

      for (let index = 0; index < 42; index += 1) {
         const currentDate = new Date(startDate);
         currentDate.setDate(startDate.getDate() + index);
         const key = formatDateKey(currentDate);

         days.push({
            key,
            date: currentDate,
            label: currentDate.getDate(),
            isCurrentMonth: currentDate.getMonth() === calendarMonth.getMonth(),
            isToday: key === formatDateKey(new Date()),
            isSelected: key === selectedDateKey,
            hasEvents: Boolean(eventsByDate[key])
         });
      }

      return days;
   }, [calendarMonth, eventsByDate, selectedDateKey]);

   const calendarWeeks = useMemo(() => {
      const weeks = [];
      for (let i = 0; i < calendarDays.length; i += 7) {
         weeks.push(calendarDays.slice(i, i + 7));
      }
      return weeks;
   }, [calendarDays]);

   const selectedDayEvents = selectedDateKey ? eventsByDate[selectedDateKey] || [] : [];
   const selectedDateLabel = selectedDateKey ? formatFullDateLabel(parseDateKey(selectedDateKey)) : t('community.upcomingEvents.selectADay');
   const calendarMonthLabel = `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`;
   const dayNames = [
      t('community.upcomingEvents.daySun', 'Sun'),
      t('community.upcomingEvents.dayMon', 'Mon'),
      t('community.upcomingEvents.dayTue', 'Tue'),
      t('community.upcomingEvents.dayWed', 'Wed'),
      t('community.upcomingEvents.dayThu', 'Thu'),
      t('community.upcomingEvents.dayFri', 'Fri'),
      t('community.upcomingEvents.daySat', 'Sat'),
   ];

   const calendarNavButtonStyle = {
      border: '1px solid #E4EBF6',
      backgroundColor: '#FFFFFF',
      borderRadius: '50%',
      width: 36,
      height: 36,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
   };

   const getDayButtonStyles = (day) => ({
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: day.isSelected ? `2px solid ${brandColor}` : '1px solid transparent',
      backgroundColor: day.isSelected ? brandColor : day.isToday ? 'rgba(110, 137, 175, 0.14)' : 'transparent',
      color: day.isSelected ? '#ffffff' : day.isCurrentMonth ? '#1D1D1D' : '#C6D3E6',
      fontWeight: day.isSelected || day.isToday ? 700 : 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
   });

   const getViewToggleStyles = (isActive) => ({
      border: isActive ? `1.5px solid ${brandColor}` : '1.5px solid #E4EBF6',
      backgroundColor: '#FFFFFF',
      borderRadius: 10,
      padding: 8,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease'
   });

   const handleEventsViewChange = (view) => {
      setEventsView(view);
   };

   const handleMonthChange = (offset) => {
      setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
   };

   const handleDaySelect = (day) => {
      setSelectedDateKey(day.key);

      if (!day.isCurrentMonth) {
         const nextMonthDate = parseDateKey(day.key);
         setCalendarMonth(new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth(), 1));
      }
   };

   // Generate Google Calendar URL for adding event
   const generateGoogleCalendarUrl = (event) => {
      const startDate = new Date(event.start);
      const endDate = event.end ? new Date(event.end) : new Date(startDate.getTime() + 60 * 60 * 1000);
      
      // Format dates as YYYYMMDDTHHMMSSZ (UTC)
      const formatGoogleDate = (date) => {
         const year = date.getUTCFullYear();
         const month = String(date.getUTCMonth() + 1).padStart(2, '0');
         const day = String(date.getUTCDate()).padStart(2, '0');
         const hours = String(date.getUTCHours()).padStart(2, '0');
         const minutes = String(date.getUTCMinutes()).padStart(2, '0');
         const seconds = String(date.getUTCSeconds()).padStart(2, '0');
         return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
      };

      const startDateStr = formatGoogleDate(startDate);
      const endDateStr = formatGoogleDate(endDate);
      
      // Format meeting link - normalize Zoom and other platform links
      let meetingLink = event.link || '';
      if (meetingLink) {
         // Normalize Zoom links
         if (event.platform && event.platform.toLowerCase().includes('zoom')) {
            // Handle different Zoom link formats
            if (meetingLink.includes('zoom.us/j/') || meetingLink.includes('zoom.us/s/')) {
               // Already has zoom.us, just ensure https://
               if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
                  meetingLink = `https://${meetingLink}`;
               }
            } else if (meetingLink.includes('web.zoom.us')) {
               // web.zoom.us format, convert to zoom.us
               if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
                  meetingLink = `https://${meetingLink}`;
               }
               meetingLink = meetingLink.replace('web.zoom.us', 'zoom.us');
            } else if (/^\d+$/.test(meetingLink.trim())) {
               // Just a meeting ID
               meetingLink = `https://zoom.us/j/${meetingLink.trim()}`;
            } else {
               // Generic zoom link format
               if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
                  meetingLink = `https://zoom.us/j/${meetingLink}`;
               }
            }
         } else if (event.platform && event.platform.toLowerCase().includes('google meet')) {
            // Normalize Google Meet links
            if (meetingLink.includes('meet.google.com')) {
               if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
                  meetingLink = `https://${meetingLink}`;
               }
            } else {
               // Assume it's a meeting code
               if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
                  meetingLink = `https://meet.google.com/${meetingLink}`;
               }
            }
         } else if (event.platform && event.platform.toLowerCase().includes('microsoft teams')) {
            // Normalize Microsoft Teams links
            if (meetingLink.includes('teams.microsoft.com')) {
               if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
                  meetingLink = `https://${meetingLink}`;
               }
            } else {
               // Teams links might be in different formats
               if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
                  meetingLink = `https://${meetingLink}`;
               }
            }
         } else {
            // Generic link - just add https:// if not present
            if (!meetingLink.startsWith('http://') && !meetingLink.startsWith('https://')) {
               meetingLink = `https://${meetingLink}`;
            }
         }
      }
      
      // Build location string
      let location = '';
      if (meetingLink) {
         location = meetingLink;
      } else if (event.platform) {
         location = `${event.platform} Meeting`;
      }
      
      // Build details with link if available
      let details = event.description || '';
      if (meetingLink && details) {
         details = `${details}\n\nJoin: ${meetingLink}`;
      } else if (meetingLink) {
         details = `Join: ${meetingLink}`;
      }
      
      // Build the Google Calendar URL
      const params = new URLSearchParams({
         action: 'TEMPLATE',
         text: event.title,
         dates: `${startDateStr}/${endDateStr}`,
         details: details,
         location: location
      });

      return `https://calendar.google.com/calendar/render?${params.toString()}`;
   };

   const handleAddToCalendar = (event, e) => {
      e.preventDefault();
      e.stopPropagation();
      if (event.is_example) return;
      const calendarUrl = generateGoogleCalendarUrl(event);
      window.open(calendarUrl, '_blank');
   };

   const renderEventCard = (event, { variant = 'default' } = {}) => {
      const startDate = new Date(event.start);
      const endDate = event.end ? new Date(event.end) : null;
      const isCompact = variant === 'compact';
      const isVirtual = event.type === 'virtual';
      const accentColor = isVirtual ? brandColor : '#2F9E5B';

      const truncateText = (text, maxLength = 60) => {
         if (text.length <= maxLength) {
            return text;
         }
         return text.substr(0, maxLength) + '...';
      };

      // Helper to get platform type (for bgs)
      const getVirtualPlatformType = () => {
         if (!isVirtual) return null;
         const platform = (event.platform || '').toLowerCase();
         if (platform.includes('zoom')) return 'zoom';
         if (platform.includes('microsoft teams') || platform.includes('teams')) return 'teams';
         if (platform.includes('google meet') || platform.includes('google')) return 'meet';
         return null;
      };

      const getPlatformIcon = () => {
         if (!isVirtual) {
            return <TbMapPin style={{ color: accentColor, fontSize: '24px' }} />;
         }

         const platform = event.platform?.toLowerCase() || '';

         if (platform.includes('google meet') || platform.includes('google')) {
            return (
               <img
                  src={GoogleMeetImage}
                  alt={t('community.composer.googleMeet')}
                  style={{ width: 28, height: 28, objectFit: 'contain' }}
               />
            );
         } else if (platform.includes('zoom')) {
            return (
               <img
                  src={ZoomImageImage}
                  alt={t('profile.zoom')}
                  style={{ width: 36, height: 36, objectFit: 'contain' }}
               />
            );
         } else if (platform.includes('microsoft teams') || platform.includes('teams')) {
            return (
               <img
                  src={MicrosoftTeamsImage}
                  alt={t('community.composer.microsoftTeams')}
                  style={{ width: 28, height: 28, objectFit: 'contain' }}
               />
            );
         } else {
            return <TbVideo style={{ color: accentColor, fontSize: '24px' }} />;
         }
      };

      // Get specified background color per virtual platform type
      const getIconBackground = () => {
         if (isVirtual) {
            const type = getVirtualPlatformType();
            if (type === 'zoom') {
               return '#E6F1FF';
            } else if (type === 'teams') {
               return '#E7E8FF';
            } else if (type === 'meet') {
               return '#FFF5DC';
            } else {
               // fallback for other virtuals
               return 'rgba(110, 137, 175, 0.08)';
            }
         } else {
            return 'rgba(47, 158, 91, 0.08)';
         }
      };

      return (
         <div
            key={event.id}
            style={{
               border: event.is_example ? '2px dashed #D1D5DB' : '1px solid #E4EBF6',
               borderRadius: '12px',
               backgroundColor: '#FFFFFF',
               padding: '14px 16px',
               position: 'relative',
               ...(event.is_example ? { animation: 'exampleEventBorderPulse 2.5s ease-in-out infinite', overflow: 'visible' } : {})
            }}
         >
            {event.is_example && (
               <>
                  <style>{`@keyframes exampleEventBorderPulse { 0%, 100% { border-color: #D1D5DB; box-shadow: 0 0 0px rgba(165, 180, 252, 0); } 50% { border-color: #A5B4FC; box-shadow: 0 0 12px rgba(165, 180, 252, 0.4); } }`}</style>
                  <span style={{
                     position: 'absolute', top: '-8px', right: '12px',
                     fontSize: '11px', fontWeight: 600, color: '#8B7355',
                     backgroundColor: '#F0ECFF', padding: '2px 10px', borderRadius: '10px',
                     zIndex: 1,
                  }}>
                     Example
                  </span>
               </>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
               {/* Event icon */}
               <div
                  style={{
                     width: '48px',
                     height: '48px',
                     borderRadius: '12px',
                     backgroundColor: getIconBackground(),
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     flexShrink: 0
                  }}
               >
                  {getPlatformIcon()}
               </div>

               {/* Main content right of icon */}
               <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                     style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1D1D1D',
                        lineHeight: 1.3,
                        letterSpacing: '-0.3px'
                     }}
                  >
                     {stripHtmlTags(event.title)}
                  </p>
                  <span style={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500 }}>
                     {formatEventDateLabel(startDate)} • {formatEventTimeRange(startDate, endDate)}
                  </span>
               </div>

               {/* Add to Calendar button */}
               {isVirtual && !event.is_example && (
               <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => handleAddToCalendar(event, e)}
                  style={{
                     textTransform: 'none',
                     backgroundColor: '#F2F4FF',
                     minWidth: '20px',
                     borderRadius: 8,
                     padding: '6px 8px',
                     boxShadow: 'none',
                     flexShrink: 0,
                  }}
                  title={t('community.upcomingEvents.addToGoogleCalendar')}
               >
                  <TbCalendarPlus style={{ fontSize: '16px', color: 'var(--brand-color)' }} />
               </Button>
               )}
            </div>
         </div>
      );
   };

   return (
      <div
         style={{
            backgroundColor: '#fff',
            border: '1px solid #E4EBF6',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 16px 40px rgba(24, 39, 75, 0.06)',
            marginTop: 0
         }}
      >
         <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
            <div>
               <p style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#1D1D1D' }}>{t('community.upcomingEvents.title')}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative', zIndex: windowWidth < 600 ? 50 : 999 }}>
               <button
                  type="button"
                  onClick={() => handleEventsViewChange('list')}
                  style={getViewToggleStyles(eventsView === 'list')}
                  aria-label={t('community.showListView')}
               >
                  <TbList style={{ color: eventsView === 'list' ? brandColor : '#6E89AF' }} />
               </button>
               <button
                  type="button"
                  onClick={() => handleEventsViewChange('calendar')}
                  style={getViewToggleStyles(eventsView === 'calendar')}
                  aria-label={t('community.showCalendarView')}
               >
                  <TbCalendar style={{ color: eventsView === 'calendar' ? brandColor : '#6E89AF' }} />
               </button>
               {windowWidth >= 600 && is_me === true && (
                  <button
                     type="button"
                     onClick={() => {
                        if (onAddEvent) {
                           onAddEvent();
                        }
                     }}
                     style={getViewToggleStyles(false)}
                     aria-label={t('community.createNewEvent')}
                  >
                     <TbPlus style={{ color: '#6E89AF' }} />
                  </button>
               )}
            </div>
         </div>
         {eventsView === 'list' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
               {upcomingEvents.length ? (
                  upcomingEvents.map((event) => renderEventCard(event))
               ) : (
                  <div
                     style={{
                        border: '1px dashed #C6D3E6',
                        borderRadius: 18,
                        padding: '32px 24px',
                        textAlign: 'center',
                        color: '#6E89AF',
                        fontSize: 14,
                        backgroundColor: '#F7FAFF'
                     }}
                  >
                     {is_me ? t('community.upcomingEvents.noEventsCreator') : t('community.upcomingEvents.noEvents')}
                  </div>
               )}
            </div>
         ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
               <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                     <button type="button" onClick={() => handleMonthChange(-1)} style={calendarNavButtonStyle} aria-label={t('community.previousMonth')}>
                        <TbChevronLeft style={{ color: '#6E89AF' }} />
                     </button>
                     <p style={{ margin: 0, fontWeight: 700, color: '#1D1D1D', fontSize: 16 }}>{calendarMonthLabel}</p>
                     <button type="button" onClick={() => handleMonthChange(1)} style={calendarNavButtonStyle} aria-label={t('community.nextMonth')}>
                        <TbChevronRight style={{ color: '#6E89AF' }} />
                     </button>
                  </div>
                  <div
                     style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                        gap: 6,
                        marginTop: 18,
                        color: '#6E89AF',
                        fontSize: 12,
                        fontWeight: 600,
                        textAlign: 'center'
                     }}
                  >
                     {dayNames.map((dayName) => (
                        <div key={dayName}>{dayName}</div>
                     ))}
                  </div>
                  <div
                     style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                        gap: 8,
                        marginTop: 8
                     }}
                  >
                     {calendarWeeks.map((week, weekIdx) =>
                        week.map((day) => (
                           <div key={`${weekIdx}-${day.key}`} style={{ textAlign: 'center' }}>
                              <button type="button" onClick={() => handleDaySelect(day)} style={getDayButtonStyles(day)}>
                                 {day.label}
                              </button>
                              <span
                                 style={{
                                    display: 'inline-block',
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    marginTop: 6,
                                    backgroundColor: day.hasEvents ? brandColor : 'transparent',
                                    opacity: day.hasEvents ? 1 : 0,
                                    transition: 'all 0.2s ease'
                                 }}
                              />
                           </div>
                        ))
                     )}
                  </div>
               </div>
               <div style={{ flex: '1 1 280px', minWidth: 260, display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                     <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#1D1D1D', fontSize: 15 }}>{selectedDateLabel}</p>
                     <span style={{ color: '#6E89AF', fontSize: 13 }}>
                        {selectedDayEvents.length ? t('community.upcomingEvents.yourSessions') : t('community.upcomingEvents.noSessionsYet')}
                     </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                     {selectedDayEvents.length ? (
                        selectedDayEvents.map((event) => renderEventCard(event, { variant: 'compact' }))
                     ) : (
                        <div
                           style={{
                              border: '1px dashed #C6D3E6',
                              borderRadius: 18,
                              padding: '28px 24px',
                              color: '#6E89AF',
                              fontSize: 14,
                              backgroundColor: '#F6F8FB'
                           }}
                        >
                           {is_me ? t('community.upcomingEvents.noEventsPlannedCreator') : t('community.upcomingEvents.noEventsPlanned')}
                        </div>
                     )}
                  </div>
               </div>
            </div>
         )}
      </div>
   );
};

export default CommunityUpcomingEvents;

